import { useEffect, useMemo, useRef, useState } from "react";
import { fotoHochladen, fotosLoeschen } from "../dienste/fotos.js";
import { t } from "../i18n/index.js";


/* Sitzungsweiter Speicher für UI-Zustände (Filter, Sortierung, Suche)
   der Listenansichten. Liegt bewusst außerhalb des React-Baums, damit
   die Werte ein Aus- und Wiedereinhängen der Ansicht überdauern — etwa
   beim Öffnen eines Objekts und dem Zurückgehen. Kein localStorage:
   der Zustand soll pro Sitzung, nicht dauerhaft gelten. */
const ansichtSpeicher = {};


function usePersistentState(schluessel, initial) {
  const [wert, setWert] = useState(() =>
    Object.prototype.hasOwnProperty.call(ansichtSpeicher, schluessel)
      ? ansichtSpeicher[schluessel]
      : initial
  );
  const setze = (v) => setWert((prev) => {
    const next = typeof v === "function" ? v(prev) : v;
    ansichtSpeicher[schluessel] = next;
    return next;
  });
  return [wert, setze];
}


/* ── Etikettenfotos ────────────────────────────────────────────
   Zwei Träger, dieselbe Mechanik: Ein Etikett hängt normalerweise an
   einem Jahrgang, bei einer noch nicht zugeordneten Kritik aber an
   der Kritik selbst. `useFotoWahl` kennt nur „hier ist ein Foto, hier
   ist der Weg es zu ersetzen"; wo es liegt, entscheidet der Aufrufer.

   Pfade sind je Upload eindeutig, daher cachen Browser, Dienst-
   Arbeiter und CDN die Bilder dauerhaft; nur ein neues Bild wird
   geladen. Beim Austausch müssen deshalb die alten Dateien weg. */
function useFotoWahl({ id, foto, setzeFoto, alteLoeschen = true }) {
  const kameraRef = useRef(null);
  const dateiRef = useRef(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  const raeumeAuf = (alt) => {
    if (!alt || !alteLoeschen) return;
    fotosLoeschen([alt.pfad, alt.thumb].filter(Boolean));
  };

  const gewaehlt = async (e) => {
    const datei = e.target.files && e.target.files[0];
    const eingabe = e.target;
    if (!datei) return;
    setLaedt(true); setFehler(null);
    try {
      const alt = foto;
      const neu = await fotoHochladen(id, datei);
      setzeFoto(neu);
      raeumeAuf(alt);
    } catch (err) {
      setFehler((err && err.message) || "Der Upload ist fehlgeschlagen.");
    } finally {
      setLaedt(false);
      if (eingabe) eingabe.value = "";
    }
  };

  const entfernen = async () => {
    const alt = foto;
    setzeFoto(null);
    raeumeAuf(alt);
  };

  const kamera = () => kameraRef.current && kameraRef.current.click();
  const datei = () => dateiRef.current && dateiRef.current.click();

  /* Zwei Eingaben: mit capture öffnet das Gerät direkt die Kamera,
     ohne capture die Datei-/Galerieauswahl. */
  const eingaben = (
    <>
      <input ref={kameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={gewaehlt} aria-hidden="true" />
      <input ref={dateiRef} type="file" accept="image/*" style={{ display: "none" }} onChange={gewaehlt} aria-hidden="true" />
    </>
  );

  return { foto, eingaben, kamera, datei, entfernen, laedt, fehler };
}


/** Etikettenfoto eines Jahrgangs. Kapselt die Dateiauswahl (Kamera
    oder Galerie/Datei) und liefert über `menue` die Einträge für das
    Zeilenmenü der Jahrgangsansicht. `rundumAufnehmen` öffnet die
    Aufnahme des Rundum-Etiketts (fehlt sie, fehlt der Eintrag). */
function useEtikett(wein, jg, setDb, rundumAufnehmen = null) {
  const wahl = useFotoWahl({
    id: jg.id,
    foto: jg.foto,
    setzeFoto: (foto) => setDb((prev) => ({
      ...prev,
      weine: prev.weine.map((w) => w.id !== wein.id ? w : {
        ...w, jahrgaenge: w.jahrgaenge.map((j) => j.id !== jg.id ? j : { ...j, foto }),
      }),
    })),
  });

  const menue = [
    { label: jg.foto ? "Neu aufnehmen" : "Foto aufnehmen", onClick: wahl.kamera },
    { label: jg.foto ? "Anderes Bild wählen" : "Bild hochladen", onClick: wahl.datei },
    ...(rundumAufnehmen && !jg.rundum ? [{ label: t("Rundum aufnehmen (3D)"), onClick: rundumAufnehmen }] : []),
    ...(jg.foto ? [{ label: "Etikett entfernen", onClick: wahl.entfernen, gefahr: true }] : []),
  ];

  return { ...wahl, menue };
}

/** Verzögert einen Wert.

    Für Sucheingaben: Das Feld soll bei jedem Tastendruck reagieren,
    die Liste dahinter aber nicht. Bei 3600 Zeilen kostete jedes
    Zeichen eine vollständige Neuberechnung samt Neuaufbau der
    Tabelle — gemessen 750 bis 900 ms, also mehrere Zeichen Rückstand
    schon beim normalen Tippen.

    Der erste Wert wird NICHT verzögert: Beim Aufbau der Seite steht
    die gespeicherte Suche sofort, sonst blitzte einmal die
    ungefilterte Liste auf. */
function useEntprellt(wert, ms = 150) {
  const [spaet, setSpaet] = useState(wert);
  useEffect(() => {
    if (wert === spaet) return;
    const zeit = setTimeout(() => setSpaet(wert), ms);
    return () => clearTimeout(zeit);
  }, [wert, ms]);
  return spaet;
}


/** Lange Listen wachsen beim Scrollen nach.

    Es wird nichts geladen — der ganze Keller liegt bereits im
    Speicher. Begrenzt wird allein, wie viele Zeilen gleichzeitig im
    DOM stehen: 3000 Tabellenzeilen auf einmal aufzubauen kostete beim
    Start mehrere Sekunden, und jede Neuberechnung zahlte den Preis
    erneut.

    Bewusst Nachwachsen und keine echte Virtualisierung: Die Zeilen
    sind unterschiedlich hoch (gemessen 75 bis 123 px — lange
    Weinnamen brechen um, mehrere Kritiken stapeln sich). Ein Fenster
    mit fester Zeilenhöhe wäre daran gescheitert, und eines mit
    Nachmessen ist die Sorte Bauteil, die mit springender
    Bildlaufleiste auffällt.

    Bei einer neuen Liste — anderer Filter, andere Sortierung — geht
    es wieder bei der ersten Portion los. Alles andere wäre
    verwirrend: Man filtert, um weniger zu sehen, nicht um an
    derselben Stelle zu bleiben.

    Zwei Größen und nicht eine, weil sie verschiedene Fragen
    beantworten:

    - `erste` bestimmt, was ein Ansichtswechsel kostet. Beim Wechsel
      von der Keller- in die Weinansicht wird die alte Tabelle
      abgeräumt und die neue gebaut; gemessen rund 1,3 ms je Zeile.
      Mit 150 Zeilen dauert das ~250 ms, mit 50 noch ~70 ms. Fünfzig
      Zeilen sind auf dem Telefon etwa fünf Bildschirme — der Vorrat
      reicht, bis der Nachschub greift.
    - `schritt` bestimmt, wie oft beim Scrollen nachgelegt wird.
      Größer heißt seltener; hier ist der Bildschirm bereits gefüllt
      und niemand wartet. */
function useNachwachsen(liste, { erste = 50, schritt = 150 } = {}) {
  const [anzahl, setAnzahl] = useState(erste);
  const marke = useRef(null);

  useEffect(() => { setAnzahl(erste); }, [liste, erste]);

  useEffect(() => {
    if (anzahl >= liste.length) return;
    const el = marke.current;
    if (!el) return;
    /* Ohne Beobachter (sehr alte Browser) lieber alles zeigen als
       eine Liste, an deren Ende man nicht kommt. */
    if (typeof IntersectionObserver === "undefined") { setAnzahl(liste.length); return; }
    const beob = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          setAnzahl((n) => Math.min(n + schritt, liste.length));
        }
      },
      /* Vorlauf, damit der Nachschub schon unterwegs ist, bevor das
         Listenende sichtbar wird. */
      { rootMargin: "800px" });
    beob.observe(el);
    return () => beob.disconnect();
  }, [liste, anzahl, schritt]);

  const alles = anzahl >= liste.length;
  /* Der Ausschnitt MUSS gemerkt werden. Ein frisches `slice` bei
     jedem Render hätte sonst jedes Mal eine neue Kennung, und die
     gemerkten Zeilenelemente in den Ansichten wären wertlos — genau
     der Aufwand, den das Nachwachsen einsparen soll. */
  const sichtbar = useMemo(
    () => (alles ? liste : liste.slice(0, anzahl)),
    [liste, anzahl, alles]);

  return { sichtbar, marke, alles, gesamt: liste.length };
}


/** Zeilenelemente bauen und die bereits gebauten behalten, solange
    die Liste nur wächst.

    Ohne das kostet jeder Nachschub mehr als der davor: Beim
    Nachwachsen wird die gemerkte Elementliste neu berechnet, und
    React vergleicht ALLE bereits gerenderten Zeilen, nicht nur die
    neuen. Gemessen: 150 → 300 Zeilen kostete 256 ms, 450 → 600
    bereits 469 ms — linear wachsend, bei 3000 Zeilen wären es
    Sekunden.

    Bleiben die Elementobjekte dieselben, überspringt React sie beim
    Vergleich vollständig. Genau das macht dieser Zwischenspeicher:
    Er hängt nur den Zuwachs an.

    `bau` darf absichtlich NICHT in die Abhängigkeiten — es ist an
    jeder Aufrufstelle eine frisch erzeugte Funktion. Wovon der Bau
    wirklich abhängt, gibt der Aufrufer über `deps` an; ändert sich
    dort etwas, wird alles neu gebaut. */
function useWachsendeElemente(liste, sichtbar, bau, deps) {
  const zwischen = useRef({ liste: null, deps: null, elemente: [] });
  return useMemo(() => {
    const z = zwischen.current;
    const unveraendert = z.liste === liste
      && z.deps !== null && z.deps.length === deps.length
      && deps.every((d, i) => d === z.deps[i]);

    const elemente = unveraendert && z.elemente.length <= sichtbar.length
      ? z.elemente.concat(sichtbar.slice(z.elemente.length).map(bau))
      : sichtbar.map(bau);

    zwischen.current = { liste, deps, elemente };
    return elemente;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [liste, sichtbar, ...deps]);
}


export { ansichtSpeicher, useEntprellt, useNachwachsen, useWachsendeElemente, useEtikett, useFotoWahl, usePersistentState };
